import { useQuery } from "@tanstack/react-query";

import { getSignedMeetupVideoUrl } from "@/features/video/upload";
import { getOrganizerVideoUrl } from "@/lib/api/organizer-actions";
import { Avatar } from "./Avatar";
import { cn } from "@/lib/utils";

type Mode = "anon" | "organizer";

interface VideoAvatarProps {
  /** Display name — used as the initials fallback. */
  name: string;
  size?: number;
  bordered?: boolean;
  className?: string;
  /** Storage path of the video. When absent we fall back to initials. */
  videoStoragePath?: string | null;
  /** Event id — required for the anon mode signed URL. */
  eventId?: string;
  /** The viewer's own participantId — required for the anon mode signed URL. */
  viewerParticipantId?: string;
  /** Anon (= viewer is a participant) or organizer (= authenticated user). */
  mode?: Mode;
  /** Disable autoplay. Defaults to muted+autoplay+loop. */
  noAutoplay?: boolean;
  /** Show the small "play" badge in the corner. */
  showPlayBadge?: boolean;
}

/**
 * Round avatar that becomes a looping muted video when a pitch video is
 * available. Falls back to initials when there's no path or the signed URL
 * can't be issued. Used everywhere we previously rendered <Avatar/> for a
 * pitched participant — the brand calls for circle video, not flat icons.
 */
export function VideoAvatar({
  name,
  size = 56,
  bordered,
  className,
  videoStoragePath,
  eventId,
  viewerParticipantId,
  mode = "anon",
  noAutoplay,
  showPlayBadge,
}: VideoAvatarProps) {
  const enabled =
    Boolean(videoStoragePath) &&
    (mode === "organizer"
      ? true
      : Boolean(eventId && viewerParticipantId));

  const { data: signedUrl } = useQuery({
    queryKey: [
      "video-avatar",
      mode,
      eventId ?? "",
      viewerParticipantId ?? "",
      videoStoragePath ?? "",
    ],
    queryFn: async () => {
      if (!videoStoragePath) return null;
      if (mode === "organizer") {
        return await getOrganizerVideoUrl(videoStoragePath);
      }
      if (!eventId || !viewerParticipantId) return null;
      return await getSignedMeetupVideoUrl({
        eventId,
        participantId: viewerParticipantId,
        targetPath: videoStoragePath,
      });
    },
    enabled,
    staleTime: 4 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });

  if (!signedUrl) {
    return <Avatar name={name} size={size} bordered={bordered} className={className} />;
  }

  return (
    <div
      className={cn("relative shrink-0", className)}
      style={{ width: size, height: size }}
    >
      <div
        className={cn(
          "h-full w-full overflow-hidden rounded-full bg-black",
          bordered && "ring-2 ring-white shadow-[0_0_0_1px_#e2e8f0]",
        )}
      >
        <video
          // `#t=0.1` media fragment so the browser decodes a real first
          // frame for the poster instead of leaving the canvas black on
          // iOS Safari.
          src={signedUrl.includes("#t=") ? signedUrl : `${signedUrl}#t=0.1`}
          autoPlay={!noAutoplay}
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            try {
              if (v.currentTime < 0.05) v.currentTime = 0.1;
            } catch {
              /* ignore — some streams reject early seek */
            }
          }}
          className="h-full w-full object-cover"
        />
      </div>
      {showPlayBadge ? (
        <div
          className="absolute -bottom-0.5 -right-0.5 grid place-items-center rounded-full border-2 border-white bg-[#2563eb] text-white shadow-sm"
          style={{
            width: Math.max(20, Math.round(size * 0.28)),
            height: Math.max(20, Math.round(size * 0.28)),
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-1/2 w-1/2"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      ) : null}
    </div>
  );
}
